import { call, put, takeLatest } from 'redux-saga/effects'
import { updateAuthData, updateAuthError, updateAuthIsLoading } from './AuthSlice'
import { runtimeConfig } from '@/runtime-config'
import mockSuccessLogin from './mocks/success.json'

export const authActions = {
  LOGIN: 'LOGIN',
  LOGIN_WITH_GOOGLE: 'LOGIN_WITH_GOOGLE',
  LOGOUT: 'LOGOUT',
}

const baseUrl = runtimeConfig.APP_URL

const TELECOM = 'telecom'

const LOGIN_ENDPOINT = '/api/spa/telecom/login'
const LOGIN_GOOGLE_ENDPOINT = '/api/spa/telecom/login-google'

// Calculate end of day Paris time (23:59:59.999)
const parisEndOfDay = () => {
  const now = new Date()
  // Get Paris time offset
  const parisOffset = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }))
  parisOffset.setHours(23, 59, 59, 999)
  return parisOffset
}

function* saveSession({ data, navigate }) {
  const {
    api_token: authTokenSession,
    api_token_id: authTokenSessionId,
    loaderPaths: loaderRoutes,
    loaderPathsEnergy: loaderRoutesEnergy,
    loaderPathsInsurance: loaderRoutesInsurance,
    id: userId,
    lang,
    userLanguage,
    country,
    modulesStatuses,
    modulesStatusesEnergy,
    modulesStatusesInsurance,
    isSelectraAdmin,
    isMcpAgent,
    isAdmin,
    extraRoutes,
    isExternal,
    authorizedSites,
    commonData,
    tinymceKey,
    name,
    firstname,
    createdAt,
    team_id: teamId,
    team_name: teamName,
    team_can_create_leads_on_own_vertical: teamCanCreateLeadsOnOwnVertical,
    cut_offers: cutOffers,
  } = data

  const authData = {
    authTokenSession,
    authTokenSessionId,
    loaderRoutes,
    loaderRoutesEnergy,
    loaderRoutesInsurance,
    userId,
    lang,
    country,
    userLanguage,
    modulesStatuses,
    modulesStatusesEnergy,
    modulesStatusesInsurance,
    isSelectraAdmin,
    isMcpAgent,
    isAdmin,
    extraRoutes,
    isExternal,
    authorizedSites,
    commonData,
    tinymceKey,
    sessionExpiration: parisEndOfDay(),
    name,
    firstname,
    createdAt,
    teamId: teamId ?? null,
    teamName: teamName ?? '',
    teamCanCreateLeadsOnOwnVertical: teamCanCreateLeadsOnOwnVertical ?? false,
    cutOffers: Array.isArray(cutOffers) ? cutOffers : [],
  }

  yield put(updateAuthData({ ...authData, initialized: true }))

  const queryParameters = new URLSearchParams(window.location.search)
  const fromParam = queryParameters.get('from')

  // Only allow internal paths with a single leading slash. This rejects open-redirect
  // payloads such as `//evil.com` or `/\evil.com`, which navigate() may resolve as an
  // external URL.
  const isSafeInternalPath = path => typeof path === 'string' && /^\/(?![/\\])/.test(path)

  // Two cases:
  // 1. The user was sent to /login?from=... (expired session / logout) -> follow `from`.
  // 2. They landed directly on a protected URL (e.g. /contact/add?xxx): the login page is
  //    shown without changing the URL, so the redirect target is the current URL itself.
  const { pathname, search } = window.location
  const isLoginOrRoot = pathname === '/' || pathname === '/login'

  let nextRoute = '/'
  if (isSafeInternalPath(fromParam)) {
    nextRoute = fromParam
  } else if (!isLoginOrRoot && isSafeInternalPath(pathname)) {
    nextRoute = `${pathname}${search}`
  }

  navigate(nextRoute)
}

export function* loginSaga({ email, password, country, navigate }) {
  if (import.meta.env.DEV && email.includes('@mock.com')) {
    yield call(saveSession, { data: { ...mockSuccessLogin[email], country }, navigate })
    return
  }

  yield put(updateAuthIsLoading(true))

  const formData = new FormData()
  formData.append('email', email)
  formData.append('password', password)
  // Todo change once we update backend lang usage as lang and not country
  formData.append('lang', country)
  formData.append('country', country)
  formData.append('type', TELECOM)

  try {
    const request = yield call(fetch, `${baseUrl}${LOGIN_ENDPOINT}`, {
      method: 'post',
      mode: 'cors',
      headers: {
        Accept: 'Application/json',
      },
      body: formData,
    })

    const { status } = request
    const { data, errorMessage } = yield call([request, request.json])

    if (status === 200) {
      yield call(saveSession, { data, navigate })
    } else {
      yield put(updateAuthError(errorMessage))
    }
  } catch (error) {
    yield put(updateAuthError(JSON.parse(error)))
  } finally {
    yield put(updateAuthIsLoading(false))
  }
}

export function* loginWithGoogleSaga({ accessToken, navigate, country }) {
  yield put(updateAuthIsLoading(true))

  try {
    const formData = new FormData()
    formData.append('accessToken', accessToken.access_token)
    formData.append('type', TELECOM)
    formData.append('lang', country)
    formData.append('country', country)

    const request = yield call(fetch, `${baseUrl}${LOGIN_GOOGLE_ENDPOINT}`, {
      method: 'post',
      mode: 'cors',
      headers: {
        Accept: 'Application/json',
      },
      body: formData,
    })

    const { status } = request
    const { data, errorMessage } = yield call([request, request.json])

    if (status === 200) {
      yield call(saveSession, { data, navigate })
    } else {
      yield put(updateAuthError(errorMessage))
    }
  } catch (error) {
    yield put(updateAuthError(JSON.parse(error)))
  } finally {
    yield put(updateAuthIsLoading(false))
  }
}

export default function* authSagas() {
  yield takeLatest(authActions.LOGIN, loginSaga)
  yield takeLatest(authActions.LOGIN_WITH_GOOGLE, loginWithGoogleSaga)
}
